import { CellStyle } from 'ag-grid-community'
import { colorPalette } from '../../../style/colorPalette'
import { WorkloadUnit } from './commonColumnDef'
import { extractYearMonth } from './periodUtils'

export const ALERT_COLORS = {
  caution: colorPalette.pink[1],
  warning: colorPalette.pink[2],
}

export interface AlertThreshold {
  resourcePlanCautionThreshold: number
  resourcePlanWarningThreshold: number
  estimatedOverCautionThreshold: number
  estimatedOverWarningThreshold: number
  estimatedUnderCautionThreshold: number
  estimatedUnderWarningThreshold: number
  actualOverCautionThreshold: number
  actualOverWarningThreshold: number
  actualUnderCautionThreshold: number
  actualUnderWarningThreshold: number
}

export interface PeriodWorkingDay {
  businessDays: number
  workHours: number
}

export const calculateWeeklyMonthAlertLimit = (
  periodWorkingDays: number,
  monthWorkingDays: number
): number => {
  if (monthWorkingDays === 0) return 0
  return (1.0 * periodWorkingDays) / monthWorkingDays
}

export const calculateAlertUpperLimit = (
  workloadUnit: WorkloadUnit,
  periodData: PeriodWorkingDay
): number => {
  switch (workloadUnit) {
    case 'MONTH':
      return 1.0
    case 'DAY':
      return periodData.businessDays
    case 'HOUR':
      return periodData.workHours
    default:
      return Infinity
  }
}

export const applyCellAlertStyle = (
  value: number,
  alertUpperLimit: number,
  baseStyle: CellStyle
): CellStyle => {
  if (value > alertUpperLimit) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.caution }
  }
  return baseStyle
}

export const applyCellAlertStyleForResourcePlan = (
  value: number,
  alertUpperLimit: number,
  baseStyle: CellStyle,
  alertThreshold: AlertThreshold
): CellStyle => {
  if (
    value >
    (alertUpperLimit * alertThreshold.resourcePlanWarningThreshold) / 100
  ) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.warning }
  }
  if (
    value >
    (alertUpperLimit * alertThreshold.resourcePlanCautionThreshold) / 100
  ) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.caution }
  }
  return baseStyle
}

export const applyCellAlertStyleForEstimated = (
  value: number,
  resourcePlanValue: number,
  baseStyle: CellStyle,
  alertThreshold: AlertThreshold
): CellStyle => {
  if (value <= 0 || resourcePlanValue <= 0) return baseStyle

  const overWarningLimit =
    (resourcePlanValue * alertThreshold.estimatedOverWarningThreshold) / 100
  const underWarningLimit =
    (resourcePlanValue * alertThreshold.estimatedUnderWarningThreshold) / 100
  const overCautionLimit =
    (resourcePlanValue * alertThreshold.estimatedOverCautionThreshold) / 100
  const underCautionLimit =
    (resourcePlanValue * alertThreshold.estimatedUnderCautionThreshold) / 100

  if (value > overWarningLimit || value < underWarningLimit) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.warning }
  }
  if (value > overCautionLimit || value < underCautionLimit) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.caution }
  }
  return baseStyle
}

export const applyCellAlertStyleForActual = (
  value: number,
  estimatedValue: number,
  baseStyle: CellStyle,
  alertThreshold: AlertThreshold
): CellStyle => {
  if (value <= 0 || estimatedValue <= 0) return baseStyle

  const overWarningLimit =
    (estimatedValue * alertThreshold.actualOverWarningThreshold) / 100
  const underWarningLimit =
    (estimatedValue * alertThreshold.actualUnderWarningThreshold) / 100
  const overCautionLimit =
    (estimatedValue * alertThreshold.actualOverCautionThreshold) / 100
  const underCautionLimit =
    (estimatedValue * alertThreshold.actualUnderCautionThreshold) / 100

  if (value > overWarningLimit || value < underWarningLimit) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.warning }
  }
  if (value > overCautionLimit || value < underCautionLimit) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.caution }
  }
  return baseStyle
}

export const calculateAlertLimitFromCalendar = (
  workingDayCalendars: { [key: string]: PeriodWorkingDay },
  periodKey: string,
  workloadUnit: WorkloadUnit,
  timeGrain?: 'MONTH' | 'WEEK'
): number => {
  const periodData = workingDayCalendars[periodKey]
  if (!periodData) return Infinity

  if (workloadUnit === 'MONTH' && timeGrain === 'WEEK') {
    const yearMonth = extractYearMonth(periodKey)
    if (!yearMonth) return 1.0

    const monthData = workingDayCalendars[yearMonth]
    if (!monthData || monthData.businessDays <= 0) return 1.0

    return calculateWeeklyMonthAlertLimit(
      periodData.businessDays,
      monthData.businessDays
    )
  }

  return calculateAlertUpperLimit(workloadUnit, periodData)
}
